import {
  PromoReturnMessage,
  QVAnalyticsModel,
  RawPromotionsData,
  RawQuickviewApiData,
  SKUDetail,
  SkuDetails,
  DefiningAttribute,
  DescriptiveAttribute,
  AttributeModel,
} from './types';

/**
 * Attempts to utilize descriptiveAttributes from the style object from Product Details API to return Brand
 * @param {array} descriptiveAttributes
 */
const getProductBrand = (descriptiveAttributes: DescriptiveAttribute[]) => {
  let brand = '';
  if (descriptiveAttributes && descriptiveAttributes.length > 0) {
    const brandDescriptiveAttr = descriptiveAttributes.find(
      (element) => element.identifier === 'X_BRAND',
    );

    if (brandDescriptiveAttr && brandDescriptiveAttr.value) {
      brand = brandDescriptiveAttr.value;
    }
  }
  return brand;
};

/**
 * Attempts to utilize promoMessage object to pull this products promotional messaging data utilizing the products catentryId from ProductCatentryId
 * @param {object} promoMessage
 */
const getPromoMessage = (promotionsData: RawPromotionsData, productCatentryId: number) => {
  let promoMsg: PromoReturnMessage[] = [
    {
      desc: '',
      promotionId: '',
      type: '',
    },
  ];

  if (promotionsData && promotionsData[productCatentryId]) {
    const productPromoData = promotionsData[productCatentryId];
    if (productPromoData) {
      promoMsg = [
        {
          desc: productPromoData.promoDesc,
          promotionId: productPromoData.promotionId,
          type: productPromoData.groupName,
        },
      ];
    }
  }
  return promoMsg;
};

/**
 * Attempts to utilize definingAttributes from the style object from the Product Details API to return an analytics formatted product attribute array
 * @param {array} definingAttributes
 */
const getProductAttributeList = (definingAttributes: DefiningAttribute[]) => {
  let productAttributes: AttributeModel[] = [];
  if (definingAttributes && definingAttributes.length > 0) {
    productAttributes = definingAttributes.map((attributeGroup) => {
      // Get attribute count
      const attributes = attributeGroup.value.split('|');
      const attributeCount = Array.isArray(attributes) ? attributes.length : 1;

      return {
        CountOfOptions: attributeCount,
        Name: attributeGroup.name,
        Type: attributeGroup.name === 'Color' ? 'Swatch' : 'Blocks',
      };
    });
  }
  return productAttributes;
};

/**
 * Attempts to utilize skus from the Product Details API to return analytics formatted product sku details array
 * @param {array} skus
 */
const getProductSKUDetails = (skus: SkuDetails[]) => {
  let productSKUs: SKUDetail[] = [];
  if (skus && skus.length > 0) {
    productSKUs = skus.map((sku) => {
      let shipClass = '';
      if (sku.descriptiveAttributes && sku.descriptiveAttributes.length > 0) {
        const shipClassDescriptiveAttr = sku.descriptiveAttributes.find(
          (element) => element.identifier === 'Ship_Class',
        );

        if (shipClassDescriptiveAttr && shipClassDescriptiveAttr.value) {
          shipClass = shipClassDescriptiveAttr.value;
        }
      }

      return {
        AddtoCartAvailable: !(
          sku.isaFlag &&
          !sku.bopisFlag &&
          sku.bopisQty <= 0 &&
          sku.shipQty <= 0
        ),
        AvailableForPickup: sku.bopisFlag && sku.bopisQty > 0,
        AvailableInStore: sku.isaFlag && sku.isaQty > 0,
        AvailabletoShip: !!sku.buyable && sku.shipQty > 0,
        BOPISAvailable: sku.bopisFlag,
        ISAAvailable: sku.isaFlag,
        ProductSKU: sku.partNumber,
        SKUCurrentPrice: sku.prices.offerPrice,
        ...(sku.prices.priceIndicator === 0 ? { SKUListPrice: sku.prices.listPrice } : {}),
        ShipClass: shipClass,
      };
    });
  }
  return productSKUs;
};

export default (
  rawQuickviewApiData: RawQuickviewApiData,
  rawPromotionsData: RawPromotionsData,
  referrer: string,
  additionalAnalytics: string | object,
  quickViewTemplate: string,
) => {
  const returnValue: QVAnalyticsModel = {
    PageTemplate: 'QuickView',
    ProductAttributeList: [],
    ProductBrand: '',
    ProductCatentryId: 0,
    ProductDetails: [],
    ProductID: '',
    ProductName: '',
    ProductSKUDetails: [],
    ProductShippingRestriction: false,
    PromoMessage: [],
    ReferringPage: referrer,
    AdditionalData: null,
    QuickViewTemplate: '',
  };

  // Set Quickview Analytics Data - Based on style
  if (rawQuickviewApiData.style) {
    returnValue.ProductID = rawQuickviewApiData.style.partNumber;
    returnValue.ProductName = rawQuickviewApiData.style.name;
    returnValue.ProductCatentryId = rawQuickviewApiData.style.catentryId;
    returnValue.ProductShippingRestriction = rawQuickviewApiData.style.shippingRestricted;
    returnValue.PromoMessage = getPromoMessage(rawPromotionsData, returnValue.ProductCatentryId);
    if (rawQuickviewApiData.style.descriptiveAttributes) {
      returnValue.ProductBrand = getProductBrand(rawQuickviewApiData.style.descriptiveAttributes);
    }
    if (rawQuickviewApiData.style.definingAttributes) {
      returnValue.ProductAttributeList = getProductAttributeList(
        rawQuickviewApiData.style.definingAttributes,
      );
    }
  }

  // Set Quickview Analytics Data - Based on skus
  if (rawQuickviewApiData.skus) {
    returnValue.ProductSKUDetails = getProductSKUDetails(rawQuickviewApiData.skus);
  }

  if (
    returnValue.ProductID &&
    returnValue.ProductName &&
    returnValue.ProductBrand &&
    returnValue.ProductAttributeList &&
    returnValue.ProductSKUDetails
  ) {
    // Once all data is loaded set ProductDetails
    returnValue.ProductDetails = [
      {
        ProductAttributeList: returnValue.ProductAttributeList,
        ProductBrand: returnValue.ProductBrand,
        ProductID: returnValue.ProductID,
        ProductName: returnValue.ProductName,
        ProductSKUDetails: returnValue.ProductSKUDetails,
      },
    ];
  }

  if (additionalAnalytics) {
    returnValue.AdditionalData = additionalAnalytics;
  }

  if (quickViewTemplate) {
    returnValue.QuickViewTemplate = quickViewTemplate;
  }

  return returnValue;
};
